import { uploadImage } from '../containers/ShopifyAccountPage/ShopifyAccountPage.duck';
import { createResourceLocatorString } from './routes';
import { types as sdkTypes } from '../util/sdkLoader';
import { ProductType, TransactionProcessAlias, UnitType } from './enums';
import { currencyToGBP } from './genericHelpers';
const { Money } = sdkTypes;
const {
  getSharetribeProductPublicDataFromShopify,
  getSharetribeShippingDimensionsFromShopify,
  getSharetribeProductVariantsFromShopify,
} = require('./shopifyProductMapping');

const DEFAULT_SHIPPING_DIMENSIONS = {
  height: '14',
  length: '20',
  width: '30',
  unit: 'kg',
  distanceUnit: 'cm',
};

const getShippingDimensionsFromShopify = product => {
  return getSharetribeShippingDimensionsFromShopify({
    product,
    defaultDimensions: DEFAULT_SHIPPING_DIMENSIONS,
  });
};

const getSharetribeListingStateFromShopify = status => {
  const normalizedStatus = String(status || '').toLowerCase();
  return normalizedStatus === 'active' ? 'published' : 'draft';
};

const getCurrentStockQuantity = response => {
  const included = response?.data?.included || [];
  const stock = included.find(item => item?.type === 'stock');
  return stock?.attributes?.quantity ?? null;
};

const updateListingStockMaybe = async ({ sdk, listingId, oldTotal, newTotal }) => {
  const hasStockTotals = listingId && newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (!hasStockTotals) {
    return;
  }

  try {
    await sdk.stock.compareAndSet({ listingId, oldTotal, newTotal }, { expand: true });
  } catch (error) {
    // If stock was missing or changed between query and update, do not block product data sync.
    // The regular listing creation path and webhook sync can still correct stock later.
    console.warn(`[Shopify] Failed to sync stock for listing ${listingId}:`, error);
  }
};

const syncExistingListingStateMaybe = async ({
  sdk,
  listingId,
  currentState,
  desiredState,
  inventory,
}) => {
  if (!listingId || !desiredState || currentState === desiredState) {
    return;
  }

  try {
    if (desiredState === 'published' && currentState === 'closed' && Number(inventory) > 0) {
      await sdk.ownListings.open({ id: listingId }, { expand: true });
      return;
    }

    if (desiredState === 'published' && currentState === 'draft') {
      await sdk.ownListings.publishDraft({ id: listingId }, { expand: true });
    }
  } catch (error) {
    console.warn(`[Shopify] Failed to sync listing state for ${listingId}:`, error);
  }
};

export const navigateToListingPage = ({ id, slug, history, routeConfiguration, type }) => {
  const targetPage = 'EditListingPage';
  const path = createResourceLocatorString(targetPage, routeConfiguration, {
    id,
    slug,
    type,
    tab: 'details',
  });
  history.push(path);
};

// Helper function to convert image URL to File object
export async function urlToFile(url, filename) {
  try {
    const response = await fetch(url, {
      mode: 'cors', // Add CORS mode if needed
      headers: {
        Accept: 'image/*',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    // Validate that we got a valid image blob
    if (!blob.type.startsWith('image/')) {
      throw new Error(`Invalid content type: ${blob.type}`);
    }

    return new File([blob], filename, { type: blob.type });
  } catch (error) {
    throw error;
  }
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const uploadImageWithRetry = async (dispatch, file, maxRetries = 3) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await dispatch(uploadImage({ file }));
    } catch (error) {
      lastError = error;

      console.error(`[Shopify Sync] Image upload failed (attempt ${attempt}/${maxRetries})`, error);

      if (attempt < maxRetries) {
        await sleep(attempt * 1000);
      }
    }
  }

  throw lastError;
};

export const createListingDataArray = async ({ products, dispatch, sdk, shop, country }) => {
  const listingDataArray = [];

  for (const product of products) {
    try {
      const productId = product.id.split('/').pop();

      console.log(`[Shopify Sync] Processing product ${product.title} (${productId})`);

      const shippingDimensions = getShippingDimensionsFromShopify(product);

      const shopifyProductPublicData = getSharetribeProductPublicDataFromShopify(product);

      const { amount, currencyCode } = product.priceRangeV2.minVariantPrice;

      const { convertedAmount, convertedCurrency } = currencyToGBP(amount, currencyCode);
      const productVariants = getSharetribeProductVariantsFromShopify({
        product,
        currency: convertedCurrency,
        rate: Number(convertedAmount) / Number(amount || convertedAmount || 1),
      });
      const variantPrices = productVariants.map(variant => variant.priceInSubunits);
      const minVariantPriceInSubunits = variantPrices.length
        ? Math.min(...variantPrices)
        : Math.round(Number(convertedAmount) * 100);
      const totalVariantStock = productVariants.reduce(
        (total, variant) => total + Number(variant.stock || 0),
        0
      );

      /**
       * ------------------------------------------------
       * Upload images sequentially
       * ------------------------------------------------
       */
      const imageIds = [];

      const imageEdges = product?.images?.edges || [];

      for (const imageEdge of imageEdges) {
        try {
          const imageNode = imageEdge.node;
          const imageId = imageNode.id.split('/').pop();

          console.log(`[Shopify Sync] Uploading image ${imageId} for product ${productId}`);

          const file = await urlToFile(imageNode.originalSrc, `image-${productId}-${imageId}.jpg`);

          const uploadedImageId = await uploadImageWithRetry(dispatch, file);

          imageIds.push(uploadedImageId);

          // Small delay to avoid hammering Sharetribe image API
          await sleep(500);
        } catch (imageError) {
          console.error(`[Shopify Sync] Failed image upload for product ${productId}`, imageError);
        }
      }

      /**
       * ------------------------------------------------
       * Listing data
       * ------------------------------------------------
       */

      const sku = product?.variants?.edges?.[0]?.node?.sku || null;

      const publicData = {
        productId,
        ...(product?.tags?.length > 0 && {
          tags: product.tags,
        }),
        ...(sku && { sku }),
        ...(product?.vendor && {
          vendor: product.vendor,
        }),
        ...(product?.productType && {
          productType: product.productType,
        }),
        ...(product?.handle && {
          handle: product.handle,
        }),
        ...(shippingDimensions && {
          shippingDimensions,
        }),
        ...shopifyProductPublicData,
        ...(productVariants.length
          ? {
              productVariants,
              size: [...new Set(productVariants.map(variant => variant.size).filter(Boolean))],
            }
          : {}),
        transactionProcessAlias: TransactionProcessAlias.DEFAULT_PURCHASE,
        unitType: UnitType.ITEM,
        shop,
        shopCurrency: currencyCode,
        isShopifyProduct: true,
        country,
      };

      const desiredState = getSharetribeListingStateFromShopify(product.status);

      const listingData = {
        title: product.title,
        description: product.description || '',
        state: desiredState,
        images: imageIds,
        price: new Money(minVariantPriceInSubunits, convertedCurrency),
        publicData,
      };

      /**
       * ------------------------------------------------
       * Existing listing lookup
       * ------------------------------------------------
       */

      const response = await sdk.ownListings.query({
        pub_productId: productId,
        pub_shop: shop,
        states: 'published,closed,draft',
        include: ['currentStock'],
      });

      const existingListing = response?.data?.data?.[0];

      if (existingListing) {
        console.log(`[Shopify Sync] Updating existing listing for product ${productId}`);

        const listingId = existingListing.id;
        const stockQuantity = getCurrentStockQuantity(response);

        const { state, ...updateData } = listingData;

        await sdk.ownListings.update(
          {
            id: listingId,
            ...updateData,
          },
          {
            expand: true,
          }
        );

        await updateListingStockMaybe({
          sdk,
          listingId,
          oldTotal: stockQuantity,
          newTotal: productVariants.length > 0 ? totalVariantStock : product.totalInventory,
        });

        await syncExistingListingStateMaybe({
          sdk,
          listingId,
          currentState: existingListing.attributes.state,
          desiredState,
          inventory: product.totalInventory,
        });

        continue;
      }

      /**
       * ------------------------------------------------
       * New listing
       * ------------------------------------------------
       */

      listingDataArray.push({
        ...listingData,
        stockUpdate: {
          oldTotal: null,
          newTotal: productVariants.length > 0 ? totalVariantStock : product.totalInventory,
        },
      });

      // Small delay between products
      await sleep(300);
    } catch (productError) {
      console.error(
        `[Shopify Sync] Failed processing product ${product?.title || 'Unknown Product'}`,
        productError
      );

      // Continue syncing remaining products
      continue;
    }
  }

  return listingDataArray;
};
